import Utils from "@/utilities/Utils";
import { mapData } from "@/utilities/DataMapping";
import { IWorkPermitPerson, WorkPermitPerson } from "@/model/workPermits/WorkPermitPerson";
import { ISouthEasternFieldSet, SouthEasternFieldSet } from "@/model/workPermits/SouthEasternFieldSet";
import { ISwmScapaFieldSet, SwmScapaFieldSet } from "@/model/workPermits/SwmScapaFieldSet";
import { IWorkPermitEvent, WorkPermitEvent } from "@/model/workPermits/WorkPermitEvent";
import { WorkPermitEventType, WorkPermitFormType } from "@/model/Enums";
import { IResource, Resource } from "../Resource";

export interface IWorkPermit {
    id: string;
    siteID: string;
    numberPrefix: string;
    numberSequence: number;
    numberSuffix: number;

    jobName: string;
    jobDescription: string;
    companyName: string;
    validFrom: Date;
    validTo: Date;
    surrenderUrl: string;

    formType: WorkPermitFormType;
    attachmentResourceIDs: Array<string>;
    history: Array<IWorkPermitEvent>;

    created: Date;
    createdByUserID: string;
    lastUpdated: Date;
    lastUpdatedByUserID: string;
    deleted: Date;
    deletedByUserID: string;

    southEasternFieldSet: ISouthEasternFieldSet;
    swmScapaFieldSet: ISwmScapaFieldSet;
    persons: Array<IWorkPermitPerson>;
    resources: Array<IResource>;
}

export class WorkPermit implements IWorkPermit {

    constructor(data?: IWorkPermit) {
        if (data) this.update(data);
    }

    update(workPermitData: IWorkPermit): WorkPermit {
        mapData(workPermitData, {
            root: () => this,
            history: (data: IWorkPermitEvent) => new WorkPermitEvent(data),
            southEasternFieldSet: (data: ISouthEasternFieldSet) => this.southEasternFieldSet.update(workPermitData.formType === WorkPermitFormType.SouthEastern ? data : null),
            swmScapaFieldSet: (data: ISwmScapaFieldSet) => this.swmScapaFieldSet.update(workPermitData.formType === WorkPermitFormType.SwmScapa ? data : null),
            persons: (data: IWorkPermitPerson) => new WorkPermitPerson(data),
            resources: (data: IResource) => new Resource(data)
        });
        return this;
    }

    id: string = Utils.emptyGuidValue;
    siteID: string = Utils.emptyGuidValue;
    numberPrefix: string = "";
    numberSequence: number = 0;
    numberSuffix: number = 0;
    copiedFromID: string = "";

    jobName: string = "";
    jobDescription: string = "";
    companyName: string = "";
    validFrom: Date = new Date(Utils.emptyDateValue);
    validTo: Date = new Date(Utils.emptyDateValue);
    surrenderUrl: string = "";

    formType: WorkPermitFormType = WorkPermitFormType.None;
    attachmentResourceIDs: Array<string> = [];
    history: Array<WorkPermitEvent> = [];

    created: Date = new Date(Utils.emptyDateValue);
    createdByUserID: string = Utils.emptyGuidValue;
    lastUpdated: Date = new Date(Utils.emptyDateValue);
    lastUpdatedByUserID: string = Utils.emptyGuidValue;
    deleted: Date = new Date(Utils.emptyDateValue);
    deletedByUserID: string = Utils.emptyGuidValue;

    southEasternFieldSet = new SouthEasternFieldSet();
    swmScapaFieldSet = new SwmScapaFieldSet();
    persons: Array<WorkPermitPerson> = [];
    resources: Array<Resource> = [];

    get number(): string {
        const seq = Utils.pad(this.numberSequence, 5);
        return this.numberSuffix === 0
            ? `${this.numberPrefix}${seq}`
            : `${this.numberPrefix}${seq}-${this.numberSuffix}`;
    }

    get isReIssue(): boolean {
        return this.numberSuffix > 0 && !Utils.isEmptyId(this.copiedFromID)
    }

    get statusText(): string {
        const status = this.latestStatus;
        const text = WorkPermitEventType[status];
        if(!text) return `??? ${status}`;
        return text.replace("Expired", " (Expired)");
    }

    get latestStatus(): WorkPermitEventType  {
        const statusChanges = this.history.filter(e => e.isStatus);
        if (statusChanges?.length === 0) return WorkPermitEventType.None;
        const latest = statusChanges.reduce((prev, current) => (prev && prev.when > current.when) ? prev : current);
        return latest?.what ?? WorkPermitEventType.None;
    }

    hasHappened(event: WorkPermitEventType) {
        return this.history.filter(h => h.what === event).length > 0;
    }

    happenedLast(event: WorkPermitEventType): WorkPermitEvent {
        // - b minus a means sort descending
        // - use slice to avoid mutating base array - which causes mayhem...
        return this.history.slice().sort((a,b) => b.when?.valueOf() ?? 0 - a.when?.valueOf() ?? 0).filter(h => h.what === event)[0];
    }

    // for cancelling issued, closed (should only use this on unsaved satus!)
    removeHistory(type: WorkPermitEventType) {
        for (let i = 0; i < this.history.length; i++) {
            const event = this.history[i];
            // could check to see
            if (event.what === type) {
                this.history.splice(i, 1);
                return;
            }
        }
    }

    //
    // -- 'special' history fields
    //

    get issuedWhen(): Date {
        return this.happenedLast(WorkPermitEventType.Issued)?.when ?? new Date(Utils.emptyDateValue);
    }
    set issuedWhen(value: Date) {
        this.setWhen(value, WorkPermitEventType.Issued);
    }

    get issuedByWho(): string {
        return this.happenedLast(WorkPermitEventType.Issued)?.who ?? "";
    }
    set issuedByWho(value: string) {
        this.setWho(value, WorkPermitEventType.Issued);
    }

    get closedWhen(): Date {
        return this.happenedLast(WorkPermitEventType.Closed)?.when ?? new Date(Utils.emptyDateValue);
    }
    set closedWhen(value: Date) {
        this.setWhen(value, WorkPermitEventType.Closed);
    }

    get closedByWho(): string {
        return this.happenedLast(WorkPermitEventType.Closed)?.who ?? "";
    }
    set closedByWho(value: string) {
        this.setWho(value, WorkPermitEventType.Closed);
    }

    get signedWhen(): Date {
        return this.happenedLast(WorkPermitEventType.Signed)?.when ?? new Date(Utils.emptyDateValue);
    }
    set signedWhen(value: Date) {
        this.setWhen(value, WorkPermitEventType.Signed);
    }

    get signedBy(): WorkPermitEvent {
        return this.happenedLast(WorkPermitEventType.Signed);
    }
    get signedByWho(): string {
        return this.signedBy?.who ?? "";
    }
    set signedByWho(value: string) {
        this.setWho(value, WorkPermitEventType.Signed);
    }

    get surrenderedBy(): WorkPermitEvent {
        return this.happenedLast(WorkPermitEventType.Surrendered);
    }
    get surrenderedByWho(): string {
        return this.surrenderedBy?.who ?? "";
    }
    get surrenderedWhen(): Date {
        return this.surrenderedBy?.when ?? new Date(Utils.emptyDateValue);
    }

    private setWho(name: string, eventType: WorkPermitEventType) {
        const event = this.happenedLast(eventType);
        if(event == null) {
            this.history.push(new WorkPermitEvent({
                whoID: "",
                who: name,
                what: eventType,
                when: new Date(Utils.emptyDateValue),
                info: ""
            }));
        }
        else {
            event.who = name;
        }
    }

    private setWhen(date: Date, eventType: WorkPermitEventType) {
        const event = this.happenedLast(eventType);
        if(event == null) {
            this.history.push(new WorkPermitEvent({
                whoID: "",
                who: "",
                what: eventType,
                when: date,
                info: ""
            }));
        }
        else {
            event.when = date;
        }
    }

    get isNew(): boolean {
        return Utils.isEmptyId(this.id);
    }

    get areNoPersons() : boolean {
        return (this.persons?.length ?? 0) === 0;
    }

    get areNoResources() : boolean {
        return (this.resources?.length ?? 0) === 0;
    }
}
